import { useOutletContext, useRouteLoaderData } from '@remix-run/react';
import { useEffect, useMemo, useState } from 'react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';

import type { OutletContext } from '~/interfaces/outlet_context';

import useIsLoggedIn from './useIsLoggedIn';
import useScrollPosition from './useScrollPosition';

import type { RootLoaderData } from '~/root';

const PERCENTAGE_SCROLLED_BEFORE_SHOWING_MODAL = 40;

const makeCookieName = (pageId: string) => `email_capture_modal_closed_${pageId}`;

export const useEmailCaptureModal = ({
  showAfterPercentageScrolled = PERCENTAGE_SCROLLED_BEFORE_SHOWING_MODAL,
  cookieExpiryDays,
  pageId,
  enabled,
}: {
  pageId: string;
  enabled: boolean;
  showAfterPercentageScrolled?: number;
  cookieExpiryDays?: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isStagingPreview } = useRouteLoaderData('root') as RootLoaderData;
  const { setCookie } = useOutletContext<OutletContext>();
  const { currentModal, handleOpenModal, handleCloseModal } = useGlobalModals();

  const isLoggedIn = useIsLoggedIn();
  const { cookies } = useOutletContext<OutletContext>();

  const isPopupDisabled = !isStagingPreview && (Boolean(cookies[makeCookieName(pageId)]) || isLoggedIn);

  const { percentageScrolled } = useScrollPosition() || {
    percentageScrolled: 0,
    windowScrollY: 0,
  };
  const currentProgress = Number((percentageScrolled * 100).toFixed(1));

  const showPopup = useMemo(() => {
    if (isPopupDisabled) {
      return false;
    }

    return currentProgress >= showAfterPercentageScrolled;
  }, [currentProgress, isPopupDisabled, showAfterPercentageScrolled]);

  // Open popup
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (showPopup && !isOpen && currentModal !== 'email_capture') {
      handleOpenModal('email_capture', { preventScrollReset: true });

      if (cookieExpiryDays) {
        setCookie({ name: makeCookieName(pageId), value: 'true', days: cookieExpiryDays });
      }

      setIsOpen(true);
    }
  }, [enabled, cookieExpiryDays, currentModal, handleOpenModal, isOpen, pageId, setCookie, showPopup]);

  // Remove search param if user lands on the page with search param modal=email_capture
  // but it's not anymore applicable and the modal shouldn't be shown
  useEffect(() => {
    if (!showPopup && currentModal === 'email_capture' && !isOpen) {
      handleCloseModal({ preventScrollReset: true });
    }
  }, [showPopup, currentModal, isOpen, handleCloseModal]);

  return {
    isOpen: isOpen && currentModal === 'email_capture',
  };
};
